import type { PaymentMilestoneData } from '@/lib/api'
import { requireDataverseEtag } from '@/lib/api/dataverse-changeset'
import { calculateMilestoneAmounts } from '@/lib/payment-milestone-allocation'
import { projectPaymentMilestones } from '@/lib/payment-milestone-consistency'
import { hasAmountOnlyPaymentMilestones } from '@/lib/payment-milestone-editability'
import { getEditablePaymentMilestones } from '@/lib/payment-milestones-form'
import { isQueryLoaded } from '@/lib/plugins/plugin-readiness'
import { PaymentMilestoneStatus, QuoteStateCode, QuoteStatusCode, QuoteType } from '@/lib/types'

/** The plugin may only act once every pool input has loaded. A change order's
 * pool includes the sales-order milestones it replaces, so acting before those
 * load would see pool = delta (often 0) and delete rows. */
export function areMilestoneSourcesLoaded({
  quoteType,
  salesOrderId,
  quoteDetailsUpdatedAt,
  milestonesUpdatedAt,
  summaryUpdatedAt,
  salesOrderMilestonesUpdatedAt,
}: {
  quoteType: QuoteType | number | undefined
  salesOrderId: string | null | undefined
  quoteDetailsUpdatedAt: number
  milestonesUpdatedAt: number
  summaryUpdatedAt: number
  salesOrderMilestonesUpdatedAt: number
}) {
  const reservationsLoaded =
    quoteType !== QuoteType.ChangeOrder || !salesOrderId || isQueryLoaded(salesOrderMilestonesUpdatedAt)
  return (
    isQueryLoaded(quoteDetailsUpdatedAt) &&
    isQueryLoaded(milestonesUpdatedAt) &&
    isQueryLoaded(summaryUpdatedAt) &&
    reservationsLoaded
  )
}

export type PaymentMilestoneSyncPlan =
  | { kind: 'delete'; milestones: PaymentMilestoneData[] }
  | {
      kind: 'sync'
      deletedMilestones: PaymentMilestoneData[]
      updates: { dsy_paymentmilestoneid: string; dsy_amount: number; etag: string }[]
    }

/** Draft re-projection of a quote's own milestones onto `pool` (the quote
 * total, or a change order's delta + replaced milestones). A zero pool clears
 * the schedule. */
export function planPaymentMilestoneSync({
  milestones,
  quoteType,
  quoteStateCode,
  quoteStatusCode,
  pool,
  defaultMilestones,
}: {
  milestones: PaymentMilestoneData[]
  quoteType: QuoteType | number | undefined
  quoteStateCode: QuoteStateCode | number | undefined
  quoteStatusCode: QuoteStatusCode | number | undefined
  pool: number
  defaultMilestones: { dsy_name: string; dsy_percent: number; dsy_sequencenumber: number }[]
}): PaymentMilestoneSyncPlan | null {
  if (milestones.length === 0) return null
  const activeMilestones = milestones.filter((milestone) => milestone.statuscode !== PaymentMilestoneStatus.Inactive)
  if (hasAmountOnlyPaymentMilestones(activeMilestones)) return null
  const projection = projectPaymentMilestones({ milestones, quoteType, quoteStateCode, quoteStatusCode, total: pool })
  if (!projection.eligible) return null
  if (pool === 0) {
    return activeMilestones.length ? { kind: 'delete', milestones: activeMilestones } : null
  }

  const editableMilestones = getEditablePaymentMilestones(activeMilestones, defaultMilestones)
  const deletedMilestones = activeMilestones.filter(
    (milestone) =>
      !milestone.dsy_paylink &&
      !editableMilestones.some(
        (editableMilestone) => editableMilestone.dsy_paymentmilestoneid === milestone.dsy_paymentmilestoneid
      )
  )
  const updatedMilestones = calculateMilestoneAmounts(editableMilestones, pool)
  const updates = updatedMilestones
    .map((updatedMilestone) => {
      const originalMilestone = milestones.find(
        (milestone) => milestone.dsy_paymentmilestoneid === updatedMilestone.dsy_paymentmilestoneid
      )
      if (originalMilestone && updatedMilestone.dsy_amount !== originalMilestone.dsy_amount) {
        return {
          dsy_paymentmilestoneid: updatedMilestone.dsy_paymentmilestoneid,
          dsy_amount: updatedMilestone.dsy_amount,
          etag: requireDataverseEtag(originalMilestone),
        }
      }
      return null
    })
    .filter((update): update is NonNullable<typeof update> => update !== null)
  if (!deletedMilestones.length && !updates.length) return null
  return { kind: 'sync', deletedMilestones, updates }
}
